/**
 * Celda «Precio» reutilizable en el catálogo LLM (Sprint 285 Stream G;
 * REQ-MINT-012 §1).
 *
 * Se extiende de forma **aditiva** el catálogo (`LlmCatalogPage`,
 * `ModelManagementPage`) sin duplicar componentes: la celda lee de la
 * composición de `model-intel` (hook con caché por tenant) por `model_ref` y
 * degrada con gracia:
 *
 * - el **precio lo calcula el backend** y la celda solo lo pinta; nunca
 *   recibe coste, margen ni modo (SEC-022; ADR-119);
 * - sin dato / sin permiso de precio ⇒ «sin dato» (nunca se calcula);
 * - con dato ⇒ **precio cliente** por componente, vigencia y enlace a precios.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { useModelIntelPricingComposition } from '../hooks/useModelIntel';
import { clientPriceComponent, formatDate, formatUsd } from '../lib/pricing';

export interface ModelPriceCellProps {
  /** `model_ref` del catálogo (`llm_config`); sin él no hay cruce de precio. */
  modelRef?: string | null;
  /** Variante compacta para rejillas densas (ModelManagementPage). */
  compact?: boolean;
  /** Muestra la vigencia del precio (por defecto sí; el catálogo la oculta). */
  showValidity?: boolean;
}

export function ModelPriceCell({
  modelRef,
  compact = false,
  showValidity = true,
}: ModelPriceCellProps) {
  const { t } = useI18n();
  const { data, isLoading, isError } = useModelIntelPricingComposition();

  /**
   * Fila del modelo para la celda: un `model_ref` puede tener varias ofertas
   * (proveedores); se prefiere la que tiene **precio cliente** publicado. Si
   * ninguna tiene dato, se pinta «sin dato».
   */
  const row = useMemo(() => {
    if (!modelRef) return undefined;
    const candidates = (data?.rows ?? []).filter((candidate) => candidate.modelRef === modelRef);
    if (candidates.length === 0) return undefined;
    return candidates.find((candidate) => candidate.price?.state === 'available') ?? candidates[0];
  }, [data, modelRef]);

  if (isLoading) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-gray-400"
        data-testid={`model-price-loading-${modelRef ?? 'none'}`}
      >
        <Spinner size="sm" />
      </span>
    );
  }

  const hasPriceData =
    !isError &&
    data?.priceVisibility === 'included' &&
    row !== undefined &&
    row.price?.state === 'available';

  if (!hasPriceData) {
    if (row?.priceState === 'not_applicable') {
      return (
        <span
          className="text-xs text-gray-400 dark:text-gray-500"
          title={t('modelIntel.price.notApplicableHint')}
          data-testid={`model-price-na-${modelRef ?? 'none'}`}
        >
          {t('modelIntel.price.notApplicable')}
        </span>
      );
    }
    return (
      <span
        className="text-xs text-gray-400 dark:text-gray-500"
        title={t('modelIntel.price.hiddenHint')}
        data-testid={`model-price-nodata-${modelRef ?? 'none'}`}
      >
        {t('modelIntel.price.noData')}
      </span>
    );
  }

  const saleIn = clientPriceComponent(row.price, 'input');
  const saleOut = clientPriceComponent(row.price, 'output');

  const numberClass = 'tabular-nums text-gray-700 dark:text-gray-200';
  const labelClass = 'text-gray-500 dark:text-gray-400';

  return (
    <div
      className={compact ? 'space-y-0.5 text-[11px] leading-tight' : 'space-y-1 text-xs'}
      data-testid={`model-price-${modelRef}`}
    >
      <div className="flex flex-wrap items-center gap-x-2">
        <span className={labelClass}>{t('modelIntel.price.saleLabel')}:</span>
        {saleIn !== null || saleOut !== null ? (
          <>
            <span className={numberClass}>
              {t('modelIntel.components.input')} {formatUsd(saleIn)}
              {t('modelIntel.price.perMillion')}
            </span>
            <span className={numberClass}>
              {t('modelIntel.components.output')} {formatUsd(saleOut)}
              {t('modelIntel.price.perMillion')}
            </span>
            {row.price?.source === 'platform' && (
              <span className="text-gray-400">{t('modelIntel.price.defaultMargin')}</span>
            )}
          </>
        ) : (
          <span className="text-gray-400">{t('modelIntel.price.noData')}</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-2">
        {showValidity && (
          <span
            className="text-gray-500 dark:text-gray-400"
            title={t('modelIntel.price.sourceRate')}
          >
            {t('modelIntel.price.validFrom', { date: formatDate(row.price?.effective_from) })}
          </span>
        )}
        <Link
          className="text-blue-600 hover:underline dark:text-blue-400"
          to="/governance/model-pricing"
        >
          {t('modelIntel.price.viewPricing')}
        </Link>
      </div>
    </div>
  );
}
